import { ReactNode } from 'react';
import { Button, ListItem, Stack, Typography } from '@common-components';
import { PlacementTask, PlacementTaskStatus } from 'enums';
import { messages } from 'i18n';
import { PlacementTaskStatusIcon } from 'broker/dialogs/PlacementProcess/PlacementTaskStatusIcon';
import { PlacementTaskItemProps } from './types';

interface BasePlacementTaskItemProps extends PlacementTaskItemProps {
  children?: ReactNode;
}

export function BasePlacementTaskItem({
  task,
  getNextStatus,
  showSkipButton,
  handleStatusChange,
  handleSkip,
  isUpdating,
  children,
}: BasePlacementTaskItemProps) {
  const hasId = !!task.id;
  const isSkipped = task.status === PlacementTaskStatus.Skipped;
  const nextStatus = getNextStatus(task);

  return (
    <ListItem
      sx={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: 1,
        px: 0,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} width={1} justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1.5}>
          <PlacementTaskStatusIcon status={task.status} />
          <Stack direction="column" gap={children ? 1 : 0}>
            <Typography
              variant="body1"
              sx={{
                textDecoration: isSkipped ? 'line-through' : 'none',
                opacity: isSkipped ? 0.6 : 1,
              }}
            >
              {messages.placementProcess.placementTasks[task.placementTask as PlacementTask]}
            </Typography>
            {task.stepName && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  opacity: isSkipped ? 0.6 : 1,
                }}
              >
                {task.stepName}
              </Typography>
            )}
            {children}
            {task.status === PlacementTaskStatus.Completed && task.completedAt && (
              <Typography variant="caption" color="textSecondary">
                {messages.placementProcess.dialog.completedAt(task.completedAt)}
              </Typography>
            )}
          </Stack>
        </Stack>
        {hasId && (
          <Stack direction="row" alignItems="center" ml={1} gap={1}>
            {nextStatus && (
              <Button
                variant="contained"
                size="small"
                onClick={() => handleStatusChange(task, nextStatus)}
                disabled={isUpdating}
              >
                {messages.placementProcess.dialog.nextStepButton(nextStatus)}
              </Button>
            )}
            {showSkipButton(task) && (
              <Button variant="outlined" size="small" onClick={() => handleSkip(task)} disabled={!hasId || isUpdating}>
                {isSkipped ? messages.buttons.unSkip : messages.buttons.skip}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </ListItem>
  );
}
